import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ConnexisMark } from "@/components/brand/ConnexisMark";
import { GitBranch, LayoutDashboard, Trash2, ArrowRight, LogOut, Globe, Handshake, Building2 } from "lucide-react";

const REMEMBER_KEY = "cx_admin_home";

const DESKS = [
  {
    to: "/hub",
    kicker: "Day to day",
    label: "Connexis Hub",
    blurb: "Your lending desk — cases, the pipeline's worth, lenders, introducers, paperwork and the chat dock.",
    Icon: GitBranch,
  },
  {
    to: "/admin",
    kicker: "Lead generation",
    label: "Lead-gen admin",
    blurb: "The older side of the house: campaigns, ping-tree buyers, publishers, invoices and compliance.",
    Icon: LayoutDashboard,
  },
  {
    to: "/admin/cleanup",
    kicker: "House-keeping",
    label: "Data cleanup",
    blurb: "Clear out years of test rows area by area, decide the real-looking ones by hand, restore anything for 30 days.",
    Icon: Trash2,
  },
];

const ELSEWHERE = [
  { to: "/lender", label: "Lender portal", Icon: Building2 },
  { to: "/introducer", label: "Introducer portal", Icon: Handshake },
  { to: "/", label: "Public website", Icon: Globe },
];

export default function ChooseArea() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [remember, setRemember] = useState(false);
  const [defaultArea, setDefaultArea] = useState(() => localStorage.getItem(REMEMBER_KEY));

  // No silent forwarding. This screen used to redirect straight past itself whenever a default
  // had been remembered, which reads as "it never asked me". A default is now a badge and a
  // one-click Continue — the choice is always on screen and always undoable.
  const go = (to) => {
    if (remember) localStorage.setItem(REMEMBER_KEY, to);
    nav(to);
  };

  const forget = () => {
    localStorage.removeItem(REMEMBER_KEY);
    setDefaultArea(null);
    setRemember(false);
  };

  const signOut = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  const preferred = DESKS.find((d) => d.to === defaultArea) || null;

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#040711] px-6 py-10 text-white sm:px-10 lg:px-16"
         data-testid="choose-area">
      <div className="pointer-events-none absolute inset-0 opacity-[0.05]"
           style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
      <div className="pointer-events-none absolute -left-32 -top-40 size-[520px] rounded-full bg-[#0055FF]/20 blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-52 right-0 size-[420px] rounded-full bg-[#7da9ff]/[0.07] blur-[110px]" />

      <div className="relative mx-auto flex min-h-[calc(100vh-5rem)] w-full max-w-5xl flex-col">
        <div className="flex items-start justify-between gap-4">
          <div className="[&_span]:!text-white [&_svg]:!text-slate-900">
            <ConnexisMark mode="dark" />
          </div>
          <button onClick={signOut}
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 px-3.5 py-1.5 text-[12.5px] font-medium text-white/50 transition-colors hover:border-white/25 hover:text-white"
                  data-testid="choose-signout">
            <LogOut className="size-3.5" /> Sign out
          </button>
        </div>

        <div className="mt-16 max-w-2xl sm:mt-20">
          <div className="text-[9.5px] font-semibold uppercase tracking-[0.26em] text-[#7da9ff]">
            Signed in as {user?.email}
          </div>
          <h1 className="mt-4 text-4xl font-semibold tracking-[-0.03em] sm:text-5xl">
            Where are you working
            <span className="text-[#4d8bff]">?</span>
          </h1>
          <p className="mt-4 text-base text-white/50">
            You have the keys to both sides of Connexis. Pick one — you can move between them
            any time from the menu.
          </p>
          {preferred && (
            <div className="mt-7 flex flex-wrap items-center gap-3" data-testid="choose-preferred">
              <button onClick={() => nav(preferred.to)}
                      className="inline-flex items-center gap-2 rounded-full bg-[#0055FF] px-5 py-2.5 text-[13.5px] font-semibold text-white transition-colors hover:bg-[#2b6dff]"
                      data-testid="choose-continue">
                Continue to {preferred.label} <ArrowRight className="size-3.5" />
              </button>
              <span className="text-[12.5px] text-white/40">
                Your usual spot.{" "}
                <button onClick={forget} className="underline underline-offset-2 transition-colors hover:text-white"
                        data-testid="choose-forget">
                  Stop defaulting to it
                </button>
              </span>
            </div>
          )}
        </div>

        <div className="mt-12 grid gap-3 lg:grid-cols-3">
          {DESKS.map(({ to, kicker, label, blurb, Icon }, i) => (
            <button
              key={to}
              onClick={() => go(to)}
              style={{ animationDelay: `${90 + i * 80}ms` }}
              className="hub-rise group relative overflow-hidden rounded-2xl border border-white/[0.09] bg-white/[0.03] p-6 text-left transition-colors duration-200 hover:border-[#4d8bff]/45 hover:bg-white/[0.06]"
              data-testid={`choose-${to.replace(/\//g, "-").replace(/^-/, "")}`}
            >
              <span className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#4d8bff]/60 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              {defaultArea === to && (
                <span className="absolute right-4 top-4 rounded-full border border-[#4d8bff]/40 bg-[#0055FF]/15 px-2 py-0.5 text-[9.5px] font-semibold uppercase tracking-[0.16em] text-[#7da9ff]"
                      data-testid="choose-default-badge">
                  Your default
                </span>
              )}
              <span className="inline-grid size-10 place-items-center rounded-xl bg-[#0055FF]/15 text-[#7da9ff] transition-colors group-hover:bg-[#0055FF]/25 group-hover:text-white">
                <Icon className="size-[18px]" strokeWidth={1.7} />
              </span>
              <div className="mt-5 text-[9.5px] font-semibold uppercase tracking-[0.22em] text-white/30">{kicker}</div>
              <div className="mt-1.5 flex items-center gap-1.5 text-lg font-semibold tracking-[-0.02em]">
                {label}
                <ArrowRight className="size-4 -translate-x-1 opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100" />
              </div>
              <p className="mt-2.5 text-sm leading-relaxed text-white/45">{blurb}</p>
            </button>
          ))}
        </div>

        <label className="mt-7 inline-flex w-fit cursor-pointer items-center gap-2.5 text-[13px] text-white/45 transition-colors hover:text-white/70">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
                 className="size-3.5 accent-[#0055FF]" data-testid="choose-remember" />
          Make my pick the default — you'll still be asked, it just goes to the top
        </label>

        <div className="mt-auto flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-white/[0.07] pt-6">
          <span className="text-[9.5px] font-semibold uppercase tracking-[0.22em] text-white/25">Also yours</span>
          {ELSEWHERE.map(({ to, label, Icon }) => (
            <Link key={to} to={to}
                  className="inline-flex items-center gap-1.5 text-[13px] text-white/45 transition-colors hover:text-white"
                  data-testid={`choose-link-${label.toLowerCase().replace(/\s+/g, "-")}`}>
              <Icon className="size-3.5" /> {label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
